import { combineEpics, Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

import { Action } from '../../actions';
import {
    closeAddChatListModal,
    SAVE_ADD_CHAT_LIST_MODAL,
    SaveAddChatListModalAction,
    showErrorAddChatListModal
} from '../../actions/addChatList';
import {
    CHANGE_SEARCH_INPUT_CHAT_LIST_EDITOR,
    changeFilteredChatsChatListEditor,
    ChangeSearchInputChatListEditorAction,
    FETCH_CHATS_CHAT_LIST_EDITOR,
    fetchChatsChatListEditor,
    FetchChatsChatListEditorAction,
    showChatsErrorChatListEditor,
    updateChatsChatListEditor
} from '../../actions/chatList';
import { DialogsApiDataResult } from '../../api/dialogs/DialogsMutationExecutor';
import {
    mapChatListApiErrors,
    mapDialogsApiDataResult
} from '../../mappers/chatListEditor/chatListEditor';
import { ApiMethodExecutingError } from '../../service/Errors';
import { EpicDeps } from '../../service/StoreFactory';
import { State } from '../../typings/state';

export const fetchChatsChatListEditorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_CHATS_CHAT_LIST_EDITOR),
        switchMap(
            (action: FetchChatsChatListEditorAction): Observable<Action> => {
                const { login } = store$.value.commonData;

                return from(deps.dialogsDataProvider.getDialogs(login)).pipe(
                    map(
                        (response): Action =>
                            updateChatsChatListEditor(
                                mapDialogsApiDataResult(response)
                            )
                    ),
                    catchError(
                        (error: ApiMethodExecutingError): Observable<Action> =>
                            of(showChatsErrorChatListEditor())
                    )
                );
            }
        )
    );
};

export const changeSearchInputChatListEditorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CHANGE_SEARCH_INPUT_CHAT_LIST_EDITOR),
        debounceTime(500),
        map(
            (action: ChangeSearchInputChatListEditorAction): Action => {
                return changeFilteredChatsChatListEditor(
                    action.editorId,
                    action.searchInputText
                );
            }
        )
    );
};

export const saveAddChatListEditorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_ADD_CHAT_LIST_MODAL),
        switchMap(
            (action: SaveAddChatListModalAction): Observable<Action> => {
                const { login } = store$.value.commonData;
                const skillId = store$.value.addChatListEditor.inputText;

                return from(
                    deps.dialogsMutationExecutor.addDialog(login, { skillId })
                ).pipe(
                    switchMap(
                        (result: DialogsApiDataResult): Action[] => {
                            if (result.validationResult) {
                                return [
                                    showErrorAddChatListModal(
                                        mapChatListApiErrors(
                                            result.validationResult.errors
                                        )
                                    )
                                ];
                            }

                            return [
                                fetchChatsChatListEditor(),
                                closeAddChatListModal()
                            ];
                        }
                    ),
                    catchError(
                        (
                            error: ApiMethodExecutingError
                        ): Observable<Action> => {
                            const errors = error.getErrors();
                            const mappedErrors =
                                errors.length !== 0
                                    ? mapChatListApiErrors(errors)
                                    : [
                                          {
                                              path: '',
                                              text: error.getNativeError()
                                                  .message
                                          }
                                      ];

                            return of(showErrorAddChatListModal(mappedErrors));
                        }
                    )
                );
            }
        )
    );
};

export const chatListEditorEpic = combineEpics(
    fetchChatsChatListEditorEpic,
    changeSearchInputChatListEditorEpic,
    saveAddChatListEditorEpic
);
